import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, Loader2, User, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const inputCls = "mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute";

// Compliance status for a 'YYYY-MM-DD' expiry date: null | expired | soon (<=30d) | ok
export function docStatus(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${dateStr}T00:00:00`);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { level: "expired", days };
  if (days <= 30) return { level: "soon", days };
  return { level: "ok", days };
}

function DocPill({ label, date }) {
  const s = docStatus(date);
  if (!s) return null;
  const cls = {
    expired: "border-rig-error/50 text-rig-error bg-rig-error/10",
    soon: "border-amber-500/50 text-amber-400 bg-amber-500/10",
    ok: "border-rig-border text-rig-dim bg-rig-bg",
  }[s.level];
  const txt = s.level === "expired"
    ? `${label} expired`
    : s.level === "soon"
      ? `${label} in ${s.days}d`
      : `${label} ${date}`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] ${cls}`} data-testid={`doc-pill-${label.toLowerCase()}`}>
      {s.level !== "ok" && <AlertTriangle className="h-3 w-3" />}
      {txt}
    </span>
  );
}

function TruckDialog({ truck, drivers, onClose, onSaved }) {
  const editing = !!truck?.truck_id;
  const [unit, setUnit] = useState(truck?.unit_number || "");
  const [model, setModel] = useState(truck?.make_model || "");
  const [plate, setPlate] = useState(truck?.plate || "");
  const [driver, setDriver] = useState(truck?.driver_id || "none");
  const [reg, setReg] = useState(truck?.registration_expiry || "");
  const [ins, setIns] = useState(truck?.insurance_expiry || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        unit_number: unit, make_model: model, plate,
        driver_id: driver === "none" ? null : driver,
        registration_expiry: reg || null,
        insurance_expiry: ins || null,
      };
      if (editing) {
        await api(`/fleet/trucks/${truck.truck_id}`, { method: "PUT", body });
        toast.success("Truck updated");
      } else {
        await api("/fleet/trucks", { method: "POST", body });
        toast.success("Truck added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Could not save truck");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-rig-border bg-rig-surface" data-testid="truck-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-wide text-white">
            {editing ? "Edit Truck" : "Add a Truck"}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label className="text-sm text-rig-dim">Unit number</Label>
          <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit 12" className={inputCls} data-testid="truck-unit-input" />
          <Label className="mt-3 block text-sm text-rig-dim">Make / model (optional)</Label>
          <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Freightliner Cascadia" className={inputCls} data-testid="truck-model-input" />
          <Label className="mt-3 block text-sm text-rig-dim">Plate (optional)</Label>
          <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="OK-12345" className={inputCls} data-testid="truck-plate-input" />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="block text-sm text-rig-dim">Registration expiry</Label>
              <Input type="date" value={reg} onChange={(e) => setReg(e.target.value)} className={inputCls} data-testid="truck-reg-input" />
            </div>
            <div>
              <Label className="block text-sm text-rig-dim">Insurance expiry</Label>
              <Input type="date" value={ins} onChange={(e) => setIns(e.target.value)} className={inputCls} data-testid="truck-ins-input" />
            </div>
          </div>
          <Label className="mt-3 block text-sm text-rig-dim">Assign to driver</Label>
          <Select value={driver} onValueChange={setDriver}>
            <SelectTrigger className="mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white" data-testid="truck-driver-select">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent className="border-rig-border bg-rig-surface text-white">
              <SelectItem value="none">Unassigned</SelectItem>
              {drivers.map((d) => (
                <SelectItem key={d.user_id} value={d.user_id}>{d.name}{d.is_owner ? " (you)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !unit.trim()} data-testid="submit-truck-btn" className="h-11 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {editing ? "Save Changes" : "Add Truck"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TrucksPanel({ drivers = [], onChanged }) {
  const [trucks, setTrucks] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | truck (edit)
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async () => {
    try { setTrucks(await api("/fleet/trucks")); }
    catch { setTrucks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const nameFor = (id) => drivers.find((d) => d.user_id === id)?.name;

  const remove = async () => {
    try {
      await api(`/fleet/trucks/${removeTarget.truck_id}`, { method: "DELETE" });
      toast.success("Truck removed");
      refresh();
    } catch (e) {
      toast.error(e?.message || "Could not remove truck");
    }
  };

  return (
    <div data-testid="trucks-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">
          Trucks ({trucks?.length ?? 0})
        </p>
        <Button onClick={() => setEditing({})} data-testid="add-truck-btn" className="h-10 bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
          <Plus className="mr-1 h-4 w-4" /> Add Truck
        </Button>
      </div>

      {trucks === null ? (
        <Skeleton className="h-32 w-full bg-rig-surface2" />
      ) : trucks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-rig-borderStrong bg-rig-surface p-10 text-center" data-testid="trucks-empty">
          <Truck className="mx-auto h-8 w-8 text-rig-mute" />
          <p className="mt-3 text-sm text-rig-dim">No trucks yet. Add your first unit and assign it to a driver.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {trucks.map((t) => {
            const assigned = nameFor(t.driver_id);
            return (
              <div key={t.truck_id} className="flex items-start justify-between rounded-2xl border border-rig-border bg-rig-surface p-4" data-testid={`truck-card-${t.truck_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 shrink-0 text-rig-accent" />
                    <span className="truncate font-display text-base font-bold text-white">{t.unit_number}</span>
                  </div>
                  {t.make_model ? <p className="mt-0.5 truncate text-sm text-rig-dim">{t.make_model}</p> : null}
                  {t.plate ? <p className="mt-0.5 text-xs text-rig-mute">Plate {t.plate}</p> : null}
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <User className="h-3.5 w-3.5 text-rig-mute" />
                    <span className={assigned ? "text-white" : "text-rig-mute"}>{assigned || "Unassigned"}</span>
                  </div>
                  {(t.registration_expiry || t.insurance_expiry) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <DocPill label="Reg" date={t.registration_expiry} />
                      <DocPill label="Ins" date={t.insurance_expiry} />
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button onClick={() => setEditing(t)} data-testid={`edit-truck-${t.truck_id}`} variant="outline" size="icon" className="h-9 w-9 border-rig-borderStrong bg-transparent text-white hover:bg-rig-bg hover:text-rig-accent">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => setRemoveTarget(t)} data-testid={`delete-truck-${t.truck_id}`} variant="outline" size="icon" className="h-9 w-9 border-rig-error/50 bg-transparent text-rig-error hover:bg-rig-error/10 hover:text-rig-error">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <TruckDialog
          truck={editing}
          drivers={drivers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title={`Remove ${removeTarget?.unit_number || "truck"}?`}
        description="This truck will be deleted from your fleet. Driver data is unaffected."
        confirmLabel="Remove"
        destructive
        onConfirm={remove}
        testID="remove-truck-confirm"
      />
    </div>
  );
}
